/**
 * The workspace.
 *
 * One route, one job: open an instrument and show the evidence. The root path
 * is COMPOSITE, so `/` renders IHSG / COMPOSITE without a redirect, and `/ihsg`
 * resolves to the same instrument through the shared alias table.
 *
 * Path handling is `history.pushState` plus a `popstate` listener rather than
 * a router: there is one route shape, and a router would be more code than the
 * twelve lines below.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { apiBaseUrl, DEFAULT_METHOD, fetchChart, isAnalysisMethod, type AnalysisMethod } from "./api/client.ts";
import { fetchInstrumentCatalog, fetchScreenerCatalog, type CatalogOrigin } from "./api/catalog.ts";
import { fetchScreenerResults } from "./api/screener.ts";
import { buildChartOption } from "./chart/option.ts";
import { FlowToggle } from "./components/FlowToggle.tsx";
import { type ScreenerState } from "./components/ScreenerResultsView.tsx";
import { MobileNav } from "./components/MobileNav.tsx";
import { Sidebar } from "./components/Sidebar.tsx";
import { Workspace, type ChartState } from "./components/Workspace.tsx";
import { DEFAULT_CODE, displayNameFor, pushRecent, resolveCode, type InstrumentOption } from "./domain/instruments.ts";
import { breakpoint } from "./design/tokens.ts";
import { INSTRUMENT_FIXTURE, RECENT_FIXTURE, SCREENERS, type ScreenerEntry } from "./fixtures/instruments.ts";

function useMediaQuery(query: string): boolean {
	const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
	useEffect(() => {
		const list = window.matchMedia(query);
		const update = () => setMatches(list.matches);
		update();
		list.addEventListener("change", update);
		return () => list.removeEventListener("change", update);
	}, [query]);
	return matches;
}

/** The method is part of the address, so a refresh or a shared link keeps it. */
function methodFromSearch(search: string): AnalysisMethod {
	const requested = new URLSearchParams(search).get("method");
	return isAnalysisMethod(requested) ? requested : DEFAULT_METHOD;
}

/**
 * The address carries instrument, method and the open screener.
 *
 * A screener result is a place, not a mode: a shared link has to reopen the
 * same criterion under the same method, and a refresh must not silently drop
 * back to the chart.
 */
function addressFor(code: string, method: AnalysisMethod, screener: string | null): string {
	const path = code === DEFAULT_CODE ? "/" : `/${code}`;
	const query = new URLSearchParams();
	if (method !== DEFAULT_METHOD) query.set("method", method);
	if (screener) query.set("screener", screener);
	const search = query.toString();
	return search ? `${path}?${search}` : path;
}

function screenerFromSearch(search: string): string | null {
	return new URLSearchParams(search).get("screener") || null;
}

export function App() {
	const [code, setCode] = useState(() => resolveCode(window.location.pathname));
	const [method, setMethod] = useState<AnalysisMethod>(() => methodFromSearch(window.location.search));
	const [recents, setRecents] = useState<readonly string[]>(RECENT_FIXTURE);
	const [screener, setScreener] = useState<string | null>(() => screenerFromSearch(window.location.search));
	const [screenerState, setScreenerState] = useState<ScreenerState | null>(null);
	// Bumped by the Retry button, so a failed screener can be asked again
	// without changing the address.
	const [screenerAttempt, setScreenerAttempt] = useState(0);
	const [state, setState] = useState<ChartState>({ status: "loading" });

	const isDesktop = useMediaQuery(`(min-width: ${breakpoint.desktop}px)`);
	const isTablet = useMediaQuery(`(min-width: ${breakpoint.tablet}px)`);
	const [navOpen, setNavOpen] = useState(false);
	// Both default to expanded, so the layout opens exactly as it did before.
	// Local UI state on purpose: a collapsed panel is how you are reading right
	// now, not something a shared link should impose on someone else.
	const [navCollapsed, setNavCollapsed] = useState(false);
	const [detailsCollapsed, setDetailsCollapsed] = useState(false);
	const [catalogue, setCatalogue] = useState<{ items: readonly InstrumentOption[]; origin: CatalogOrigin }>({
		items: INSTRUMENT_FIXTURE,
		origin: "fixture",
	});
	const [screeners, setScreeners] = useState<{ items: readonly ScreenerEntry[]; origin: CatalogOrigin }>({
		items: SCREENERS,
		origin: "fixture",
	});

	// Growing past the mobile breakpoint brings the static rail back, so a
	// drawer left open would otherwise sit on top of it.
	useEffect(() => {
		if (isTablet) setNavOpen(false);
	}, [isTablet]);

	useEffect(() => {
		const onPop = () => {
			setCode(resolveCode(window.location.pathname));
			setMethod(methodFromSearch(window.location.search));
			setScreener(screenerFromSearch(window.location.search));
		};
		window.addEventListener("popstate", onPop);
		return () => window.removeEventListener("popstate", onPop);
	}, []);

	// Foreign and Whale are different analyses, not two views of one payload,
	// so changing the method refetches.
	useEffect(() => {
		let live = true;
		setState({ status: "loading" });
		fetchChart(code, { method })
			.then((result) => live && setState({ status: "ready", result }))
			.catch((error: Error) => live && setState({ status: "error", message: error.message }));
		return () => {
			live = false;
		};
	}, [code, method]);

	// Catalogues load alongside the chart, never in front of it: both helpers
	// resolve to the bundled list rather than rejecting, so a catalogue outage
	// cannot keep the workspace from rendering.
	useEffect(() => {
		let live = true;
		void fetchInstrumentCatalog().then((result) => live && setCatalogue(result));
		void fetchScreenerCatalog().then((result) => live && setScreeners(result));
		return () => {
			live = false;
		};
	}, []);

	// A screener result is a different analysis per method, so the fetch keys
	// on both — the same pair the worker keys its cache on. The client retries
	// cold requests internally; the controller here is what stops an in-flight
	// attempt, and its backoff, when the user navigates away mid-warm.
	useEffect(() => {
		if (!screener) {
			setScreenerState(null);
			return;
		}
		const controller = new AbortController();
		let live = true;
		setScreenerState({ status: "loading" });
		fetchScreenerResults(screener, method, { signal: controller.signal })
			.then((results) => live && setScreenerState({ status: "ready", results }))
			.catch((error: Error) => live && setScreenerState({ status: "error", message: error.message }));
		return () => {
			live = false;
			controller.abort();
		};
	}, [screener, method, screenerAttempt]);

	useEffect(() => {
		document.title = `${displayNameFor(code)} · Quantist`;
	}, [code]);

	// Opening a stock from anywhere — search, recents, a screener row — closes
	// the screener, because the chart is what was asked for.
	const selectInstrument = useCallback(
		(next: string) => {
			const canonical = resolveCode(next);
			setScreener(null);
			setCode(canonical);
			setRecents((current) => pushRecent(current, canonical));
			window.history.pushState({}, "", addressFor(canonical, method, null));
		},
		[method],
	);

	const selectMethod = useCallback(
		(next: AnalysisMethod) => {
			setMethod(next);
			window.history.pushState({}, "", addressFor(code, next, screener));
		},
		[code, screener],
	);

	const selectScreener = useCallback(
		(slug: string | null) => {
			setScreener(slug);
			window.history.pushState({}, "", addressFor(code, method, slug));
		},
		[code, method],
	);

	const retryScreener = useCallback(() => setScreenerAttempt((attempt) => attempt + 1), []);

	const chart = state.status === "ready" ? state.result.chart : null;
	const option = useMemo(
		() => (chart ? buildChartOption(chart, { showHolding: isTablet, compact: !isDesktop }) : null),
		[chart, isTablet, isDesktop],
	);

	// A slug in the address that the catalogue does not know still opens the
	// screener workspace: the API is the authority on what it answers, and
	// falling back to the chart would silently ignore a shared link.
	const catalogued = screener ? screeners.items.find((entry) => entry.slug === screener) : undefined;
	const activeScreener = screener
		? { label: catalogued?.label ?? screener, hint: catalogued?.hint ?? "" }
		: null;
	const flowToggle = <FlowToggle method={method} onChange={selectMethod} busy={state.status === "loading"} />;

	return (
		<div
			className="shell"
			data-nav={isTablet ? "rail" : "mobile"}
			data-mode={activeScreener ? "screener" : "chart"}
			// The grid reads these: a collapsed panel narrows its own column and
			// the released width goes to the chart.
			data-nav-collapsed={isTablet && navCollapsed ? "true" : undefined}
			data-details-collapsed={detailsCollapsed ? "true" : undefined}
		>
			{isTablet ? (
				<Sidebar
					id="desktop-nav"
					collapsed={navCollapsed}
					onToggleCollapsed={() => setNavCollapsed((open) => !open)}
					activeCode={code}
					recents={recents}
					activeScreener={screener}
					onSelectInstrument={selectInstrument}
					onSelectScreener={selectScreener}
					catalogue={catalogue.items}
					catalogueOrigin={catalogue.origin}
					screeners={screeners.items}
					screenersOrigin={screeners.origin}
				/>
			) : (
				<MobileNav
					open={navOpen}
					onOpenChange={setNavOpen}
					activeCode={code}
					recents={recents}
					activeScreener={screener}
					onSelectInstrument={selectInstrument}
					onSelectScreener={selectScreener}
					catalogue={catalogue.items}
					catalogueOrigin={catalogue.origin}
					screeners={screeners.items}
					screenersOrigin={screeners.origin}
				/>
			)}

			<Workspace
				code={code}
				method={method}
				chartState={state}
				option={option}
				screener={activeScreener}
				screenerState={screenerState}
				onSelectInstrument={selectInstrument}
				onCloseScreener={() => selectScreener(null)}
				onRetryScreener={retryScreener}
				flowToggle={flowToggle}
				apiBase={apiBaseUrl()}
				detailsCollapsed={detailsCollapsed}
				onToggleDetails={() => setDetailsCollapsed((shut) => !shut)}
			/>
		</div>
	);
}
